import type { TFunction } from 'i18next'
import { type ReactNode, useEffect, useState } from 'react'
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native'
import Animated, {
  Easing,
  Keyframe,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { AttributionWatermark } from '../components/AttributionWatermark'
import {
  AccountBalanceChip,
  type AccountBalanceChipProps,
} from '../components/v2/AccountBalanceChip'
import {
  type QuickAmountOption,
  QuickAmountsRow,
} from '../components/v2/QuickAmountsRow'
import { AmountDisplay } from '../components/v2/AmountDisplay'
import { Grabber } from '../components/Grabber'
import { useSheetFade } from '../hooks/useSheetFade'
import { NumPad } from '../components/v2/NumPad'
import {
  DepositActionMotion,
  DepositActionPhaseProvider,
} from '../components/DepositActionMotion'
import { PillButton } from '../components/PillButton'
import { FiatActionButton } from '../fiat/FiatActionButton'
import {
  isAboveMaximum,
  isBelowMinimum,
  useGuardedAmountInput,
} from '../hooks/useGuardedAmountInput'
import { usePressScale } from '../hooks/usePressScale'
import { DepositInfoCircleIcon } from '../icons/DepositInfoCircleIcon'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { formatUsd, groupThousands } from '../utils/formatAmount'
import { MOTION_DURATION, MOTION_EASING } from '../utils/motion'
import {
  DEPOSIT_ACTIONS_HEIGHT,
  DEPOSIT_METRICS,
  depositAmountCenter,
  depositMinimumHeight,
  depositNoticeOverhang,
  depositNumpadBottom,
  depositSideMargin,
} from './depositMetrics'

// The fee drawer must cover the action area that opened it.
export function depositActionsHeight(insetBottom: number): number {
  return (
    depositNumpadBottom(insetBottom) +
    DEPOSIT_METRICS.numpad.height +
    DEPOSIT_METRICS.numpad.buttonGap +
    DEPOSIT_ACTIONS_HEIGHT
  )
}

const AnimatedBox = Animated.createAnimatedComponent(Box)

export interface DepositAmountScreenProps {
  title: string
  /** Brand glyph beside the title (host-supplied). */
  brandIcon?: ReactNode
  /** Host account context in the header's trailing corner. */
  accountContext?: AccountBalanceChipProps
  /** The typed dollar amount; held by the caller so it survives navigation. */
  input: string
  onChangeInput: (input: string) => void
  /** Recommended amounts shown while the input is empty. */
  quickAmounts?: QuickAmountOption[]
  /** The payment source's maximum in dollars; a value past it is named, not refused. */
  limitUsd?: string
  /** Smallest amount the payment source takes, in dollars. */
  minLimitUsd?: string
  /** Locks `input`: disables the numpad, backspace/clear, and quick amounts. */
  disabled?: boolean
  /** The full fee line for the entered amount (e.g. `$0.50 fee`). */
  fee?: string
  /**
   * The shown fee no longer prices the current input (a keystroke invalidated
   * the quote). The fee line fades out and stops answering taps until this
   * clears with the next settled quote.
   */
  feePending?: boolean
  /**
   * Opens the fee-details drawer. Omit to leave the fee line as plain text —
   * the ⓘ then says there is more to read when there isn't.
   */
  onPressFee?: () => void
  /** Accessible name for the fee line's tap target. */
  feeAccessibilityLabel?: string
  /** Badge on the fee line's leading side (e.g. an Apple Pay mark). */
  methodBadge?: ReactNode
  /** Banner beneath the amount, e.g. an identity hold. */
  notice?: ReactNode
  /** Consent copy beneath the keypad, replacing attribution while required. */
  disclosure?: ReactNode
  submitLabel: string
  canSubmit: boolean
  onSubmit: () => void
  /** Replaces the submit pill with provider UI. */
  renderSubmit?: () => ReactNode
  amountAccessibilityLabel: string
  backspaceAccessibilityLabel: string
  /** Draw the "powered by Fun" mark at the screen's foot. */
  showAttribution?: boolean
}

/**
 * The V2 deposit surface: a full-height sheet where the amount is the whole
 * screen. Empty shows recommended-amount chips; a typed value swaps them for
 * the fee line and the submit button.
 *
 * Invalid input the screen refuses — deleting from empty or typing past the
 * source's limit — answers with a side-to-side shake, a brief warning tint and
 * a haptic. While `disabled`, the input controls themselves are non-interactive.
 */
export function DepositAmountScreen({
  title,
  brandIcon,
  accountContext,
  input,
  onChangeInput,
  quickAmounts,
  limitUsd,
  minLimitUsd,
  disabled = false,
  fee,
  feePending = false,
  onPressFee,
  feeAccessibilityLabel,
  methodBadge,
  notice,
  disclosure,
  submitLabel,
  canSubmit,
  onSubmit,
  renderSubmit,
  amountAccessibilityLabel,
  backspaceAccessibilityLabel,
  showAttribution = false,
}: DepositAmountScreenProps) {
  const theme = useTheme()
  const { t } = useFunkitTranslation()
  const SubmitButton = notice === undefined ? PillButton : FiatActionButton
  const { height: viewportHeight, width } = useWindowDimensions()
  const sideMargin = depositSideMargin(width)
  const insets = useSafeAreaInsets()
  const [noticeHeight, setNoticeHeight] = useState(0)
  const noticeOverhang = notice ? depositNoticeOverhang(noticeHeight) : 0
  const height = Math.max(
    viewportHeight,
    depositMinimumHeight(insets.top, insets.bottom) + noticeOverhang,
  )

  const {
    hasWarning,
    feedbackStyle,
    commitInput,
    handleNumpadKey,
    pulseAmount,
  } = useGuardedAmountInput({ input, onChangeInput, limitUsd, disabled })

  // The bound arrives on the amount without a keystroke behind it, so it swells
  // to show where the tap landed.
  const changeToBound = (bound: string) => {
    pulseAmount()
    onChangeInput(bound)
  }

  const belowMinimum = isBelowMinimum(input, minLimitUsd)

  // Both bounds are read off the typed value, so each lands on the keystroke
  // rather than after it, and clears the moment the amount comes back inside.
  const aboveMaximum = isAboveMaximum(input, limitUsd)

  // Takes the fee line's slot (Figma 2475:4883), and stands until the amount
  // comes back inside the bound it names.
  const boundNotice = boundMessage({
    aboveMaximum,
    belowMinimum,
    limitUsd,
    minLimitUsd,
    t,
  })

  // A zero-valued entry ('0', '0.', '0.00') is still the empty state: nothing
  // to submit yet, so the quick amounts stay up instead of a dead CTA.
  const isZeroInput = input === '' || Number(input) === 0
  const showChips = isZeroInput && !!quickAmounts?.length

  const renderAction = () => {
    // Outranks the caller's own action: there is no valid quote outside either
    // bound, so the only thing worth offering is the way back inside it.
    if (belowMinimum && minLimitUsd !== undefined) {
      return (
        <SubmitButton
          label={t('depositAmount.changeToMinimum')}
          onPress={() => changeToBound(minLimitUsd)}
        />
      )
    }
    if (aboveMaximum && limitUsd !== undefined) {
      return (
        <SubmitButton
          label={t('depositAmount.changeToMaximum')}
          onPress={() => changeToBound(limitUsd)}
        />
      )
    }
    // A quote-bearing surface has nothing valid to show outside a bound, so
    // the plain CTA keeps the slot until the offer takes it.
    if (renderSubmit && !belowMinimum && !aboveMaximum) {
      return renderSubmit()
    }
    return (
      <SubmitButton
        label={submitLabel}
        disabled={!canSubmit}
        onPress={onSubmit}
      />
    )
  }

  const content = (
    // No surface of its own: the sheet paints it (see GlassSheetBackground), and
    // a fill here would sit opaque over that glass.
    <Box style={[{ height }, { paddingTop: insets.top }]}>
      <Grabber
        fadeWithSheet
        width={DEPOSIT_METRICS.grabber.width}
        top={
          insets.top +
          DEPOSIT_METRICS.grabber.top -
          DEPOSIT_METRICS.reference.insetTop
        }
      />
      <DepositHeader
        title={title}
        brandIcon={brandIcon}
        accountContext={accountContext}
        sideMargin={sideMargin}
      />

      <AnimatedBox
        style={[
          styles.amount,
          {
            position: 'absolute',
            left: 0,
            right: 0,
            top:
              depositAmountCenter(
                height,
                insets.top,
                insets.bottom,
                noticeOverhang,
              ) -
              DEPOSIT_METRICS.amount.height / 2,
            height: DEPOSIT_METRICS.amount.height,
          },
          feedbackStyle,
        ]}
      >
        <AmountDisplay
          value={groupThousands(input)}
          hasWarning={hasWarning}
          accessibilityLabel={amountAccessibilityLabel}
        />
      </AnimatedBox>

      {/* The slack a taller device brings stays between the amount and the
          action area, so the keypad and actions hold their place. */}
      <Box flex={1} />

      <DepositActionMotion.Provider value>
        <Box style={[styles.actions, { marginHorizontal: sideMargin }]}>
          {notice === undefined ? null : (
            <Box
              onLayout={(event) =>
                setNoticeHeight(event.nativeEvent.layout.height)
              }
              style={styles.notice}
            >
              {notice}
            </Box>
          )}
          <Box
            testID="depositFeeRow"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            style={styles.feeRow}
          >
            <Box style={styles.feeLead}>{methodBadge}</Box>
            {boundNotice === undefined ? (
              fee ? (
                <FeeLine
                  fee={fee}
                  color={theme.content.elements.secondary}
                  pending={feePending}
                  onPress={onPressFee}
                  accessibilityLabel={feeAccessibilityLabel}
                />
              ) : null
            ) : (
              <Text
                weight="medium"
                style={[
                  { color: theme.content.elements.secondary },
                  styles.boundNotice,
                ]}
              >
                {boundNotice}
              </Text>
            )}
          </Box>
          {showChips ? (
            <AnimatedBox key="chips" style={styles.chips}>
              <QuickAmountsRow
                options={quickAmounts}
                onSelect={commitInput}
                disabled={disabled}
              />
            </AnimatedBox>
          ) : (
            <AnimatedBox key="submit">
              {/* Every phase the action passes through — waiting, a pill, a
                  provider's own button — shares one colour, so the swap
                  between them is a tween rather than a cut. */}
              <DepositActionPhaseProvider>
                {renderAction()}
              </DepositActionPhaseProvider>
            </AnimatedBox>
          )}
        </Box>
      </DepositActionMotion.Provider>

      {/* The design's keypad-to-edge gap, grown by the device's home indicator
          inset — but never below the minimum a gesture-nav device needs. */}
      <Box
        style={[
          styles.numpad,
          {
            marginBottom: depositNumpadBottom(insets.bottom),
            // A locked amount disables every key, and dimming the pad makes
            // that non-interactive state visible without changing its layout.
            opacity: disabled ? theme.opacity.disabled / 100 : 1,
          },
        ]}
      >
        <NumPad
          backspaceAccessibilityLabel={backspaceAccessibilityLabel}
          onPressKey={handleNumpadKey}
          disabled={disabled}
        />
      </Box>

      {disclosure ? (
        <DisclosureFooter insetBottom={insets.bottom} sideMargin={sideMargin}>
          {disclosure}
        </DisclosureFooter>
      ) : null}
      {!disclosure && showAttribution ? <AttributionWatermark /> : null}
    </Box>
  )

  return height > viewportHeight ? (
    <ScrollView style={{ height: viewportHeight }} nestedScrollEnabled>
      {content}
    </ScrollView>
  ) : (
    content
  )
}

// Reserved on both sides of the centred title, so it centres on the screen
// rather than on what the host's trailing corner leaves.
const HEADER_SLOT_WIDTH = 44

/**
 * The header's slots: the title and the host's account chip. A host that
 * supplies neither a brand mark nor account context gets the centred title the
 * Pump frames draw (2475:2827); with either one the title stays beside the
 * brand, where Fomo's header puts it.
 */
function DepositHeader({
  title,
  brandIcon,
  accountContext,
  sideMargin,
}: {
  title: string
  brandIcon?: ReactNode
  accountContext?: AccountBalanceChipProps
  sideMargin: number
}) {
  // Rides the sheet like the grabber above it.
  const fade = useSheetFade()
  if (brandIcon === undefined && accountContext === undefined) {
    return (
      <AnimatedBox
        flexDirection="row"
        alignItems="center"
        style={[styles.header, fade]}
        testID="depositHeader"
      >
        <Box style={styles.headerSlot} />
        <Text variant="headerTitle" numberOfLines={1} style={styles.flexTitle}>
          {title}
        </Text>
        <Box style={styles.headerSlot} />
      </AnimatedBox>
    )
  }

  return (
    <AnimatedBox
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      testID="depositHeader"
      style={[
        styles.header,
        {
          paddingLeft: sideMargin,
          paddingRight: DEPOSIT_METRICS.balance.avatarRight,
        },
        fade,
      ]}
    >
      <Box flexDirection="row" alignItems="center" style={styles.brand}>
        {brandIcon}
        <Text variant="headerTitle" numberOfLines={1}>
          {title}
        </Text>
      </Box>
      {accountContext ? <AccountBalanceChip {...accountContext} /> : null}
    </AnimatedBox>
  )
}

/** Long consent scrolls within its footer without moving the keypad. */
function DisclosureFooter({
  insetBottom,
  sideMargin,
  children,
}: {
  insetBottom: number
  sideMargin: number
  children: ReactNode
}) {
  return (
    <Box
      style={[
        styles.disclosure,
        {
          height: depositNumpadBottom(insetBottom),
          paddingTop: Math.min(
            DEPOSIT_METRICS.disclosure.keypadGap,
            depositNumpadBottom(insetBottom) -
              DEPOSIT_METRICS.disclosure.height,
          ),
          paddingBottom: Math.max(
            0,
            insetBottom -
              DEPOSIT_METRICS.reference.insetBottom +
              DEPOSIT_METRICS.disclosure.bottomGap,
          ),
          paddingHorizontal: sideMargin,
        },
      ]}
    >
      <ScrollView
        style={{ flex: 1, width: '100%' }}
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
        nestedScrollEnabled
      >
        {children}
      </ScrollView>
    </Box>
  )
}

/**
 * The message that takes the fee line's place — the bound the amount is outside
 * of, named with its figure. Only one can apply: a value cannot be under the
 * floor and over the ceiling at once.
 */
function boundMessage({
  aboveMaximum,
  belowMinimum,
  limitUsd,
  minLimitUsd,
  t,
}: {
  aboveMaximum: boolean
  belowMinimum: boolean
  limitUsd?: string
  minLimitUsd?: string
  t: TFunction
}): string | undefined {
  if (aboveMaximum && limitUsd !== undefined) {
    return t('depositAmount.maximumAmount', {
      amount: formatUsd(Number(limitUsd)),
    })
  }
  if (belowMinimum && minLimitUsd !== undefined) {
    return t('depositAmount.minimumAmount', {
      amount: formatUsd(Number(minLimitUsd)),
    })
  }
  return undefined
}

// Deliberately quicker and plainer than the screen's motion clock: the fee
// yielding to a stale amount is a state change, not choreography.
const FEE_FADE_TIMING = { duration: 100, easing: Easing.linear }

/**
 * The computed fee beside the payment badge, and the disclosure drawer's
 * trigger. While `pending` the whole line holds at zero opacity, with its tap
 * target disabled and itself out of the accessibility tree; the settled value
 * fades back in. The fade is the only motion fee disclosure gets — the value
 * swaps while hidden, with no animation of its own.
 */
function FeeLine({
  fee,
  color,
  pending,
  onPress,
  accessibilityLabel,
}: {
  fee: string
  color: string
  pending: boolean
  onPress?: () => void
  accessibilityLabel?: string
}) {
  const { pressStyle, onPressIn, onPressOut } = usePressScale()
  const reduceMotion = useReducedMotion()

  // Mounts hidden and fades in: the first fee is itself a reprice.
  // Reduced motion cuts to the target — the opacity still has to land.
  const visibility = useSharedValue(0)
  useEffect(() => {
    const target = pending ? 0 : 1
    visibility.value = reduceMotion
      ? target
      : withTiming(target, FEE_FADE_TIMING)
  }, [pending, reduceMotion, visibility])
  const fadeStyle = useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and esbuild
    // renames this one in the bundle (see AmountDisplay).
    'worklet'
    return { opacity: visibility.value }
  })

  const line = (
    <AnimatedBox
      flexDirection="row"
      alignItems="center"
      style={[styles.fee, fadeStyle, onPress ? pressStyle : null]}
      // An invisible line must not answer a screen reader either.
      accessibilityElementsHidden={pending}
      importantForAccessibility={pending ? 'no-hide-descendants' : 'auto'}
    >
      <Text weight="medium" style={[styles.feeText, { color }]}>
        {fee}
      </Text>
      <DepositInfoCircleIcon color={color} />
    </AnimatedBox>
  )

  if (!onPress) {
    return line
  }
  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={pending}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: pending }}
      // The line's own `accessibilityElementsHidden` hides what it contains,
      // not this button — VoiceOver reads it over a blank strip without this.
      accessible={!pending}
      // The line is a 20pt strip of text; this takes it to the 44 a finger
      // needs without moving anything around it.
      hitSlop={FEE_HIT_SLOP}
    >
      {line}
    </Pressable>
  )
}

const FEE_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 }

const styles = StyleSheet.create({
  disclosure: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: 'center',
    flexShrink: 0,
    alignItems: 'center',
  },
  // Behind every other layer of the screen, and inert.
  header: {
    paddingTop: DEPOSIT_METRICS.header.top - DEPOSIT_METRICS.reference.insetTop,
  },
  headerSlot: { width: HEADER_SLOT_WIDTH },
  flexTitle: { flex: 1 },
  brand: { gap: 12, flexShrink: 1 },
  amount: { alignItems: 'center', justifyContent: 'center' },
  notice: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: DEPOSIT_METRICS.notice.bottom,
  },
  chips: {
    marginBottom:
      DEPOSIT_METRICS.button.top +
      DEPOSIT_METRICS.button.height -
      (DEPOSIT_METRICS.chips.top + DEPOSIT_METRICS.chips.height),
  },
  actions: {
    height: DEPOSIT_ACTIONS_HEIGHT,
    justifyContent: 'flex-end',
  },
  feeRow: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: DEPOSIT_METRICS.button.height + DEPOSIT_METRICS.fee.buttonGap,
    paddingRight: DEPOSIT_METRICS.fee.rightInset,
    gap: 12,
  },
  feeText: {
    fontSize: DEPOSIT_METRICS.fee.size,
    lineHeight: DEPOSIT_METRICS.fee.lineHeight,
    letterSpacing: DEPOSIT_METRICS.fee.tracking,
  },
  // Sits in the fee's slot, so it carries the fee's type
  boundNotice: {
    fontSize: DEPOSIT_METRICS.fee.size,
    lineHeight: DEPOSIT_METRICS.fee.lineHeight,
    letterSpacing: DEPOSIT_METRICS.fee.tracking,
    textAlign: 'right',
  },
  // Let a wide payment badge yield to the fee.
  feeLead: { flexShrink: 1 },
  fee: { gap: 4 },
  numpad: {
    alignSelf: 'center',
    width: DEPOSIT_METRICS.numpad.width,
    marginTop: DEPOSIT_METRICS.numpad.buttonGap,
    height: DEPOSIT_METRICS.numpad.height,
    flexShrink: 0,
  },
})
